import React from 'react';
import { Box, Typography, Divider, CircularProgress } from '@mui/material';
import {
  AlertCircle, BookOpen, Calendar, Clock, Flame, Heart,
  Moon, Music, Sparkles, Star, Sun, Sunrise, Sunset,
} from 'lucide-react';

// -- מיפוי קטגוריות לאייקונים --------------------------------------------------
const CATEGORY_ICONS = {
  tachanun:              AlertCircle,
  hallel:                Music,
  prayerAdditions:       BookOpen,
  seasonalPrayers:       Sunrise,
  seasonalTransitions:   Sun,
  selichot:              Heart,
  weekdayPrayers:        Clock,
  shabbatAdditions:      Flame,
  motzaeiShabbat:        Moon,
  seasonalCustoms:       Star,
  liturgy:               Sparkles,
  leyning:               BookOpen,
  haftarah:              BookOpen,
  mourningPeriods:       Sunset,
  general:               Calendar,
};

// -- מיפוי קטגוריות לשמות עבריים -----------------------------------------------
const CATEGORY_LABELS = {
  tachanun:              'תחנון',
  hallel:                'הלל',
  prayerAdditions:       'תוספות תפילה',
  seasonalPrayers:       'תפילות עונתיות',
  seasonalTransitions:   'מעברים עונתיים',
  selichot:              'סליחות',
  weekdayPrayers:        'תפילות ימי חול',
  shabbatAdditions:      'תוספות שבת',
  motzaeiShabbat:        'מוצאי שבת',
  seasonalCustoms:       'מנהגים עונתיים',
  liturgy:               'פיוטים וקריאות',
  leyning:               'קריאת התורה',
  haftarah:              'הפטרה',
  mourningPeriods:       'ימי אבל ותענית',
  general:               'כללי',
};

// -- פריט בודד -----------------------------------------------------------------
const ItemRow = ({ item }) => {
  const IconComp = CATEGORY_ICONS[item.category] || Calendar;
  const isHighPriority = item.priority === 'high';

  return (
    <Box sx={{
      display: 'flex',
      alignItems: 'flex-start',
      gap: 1.25,
      py: 0.7,
      px: 1.5,
      borderRadius: '6px',
      borderRight: isHighPriority ? '3px solid #8a6d08' : '3px solid transparent',
      background: isHighPriority
        ? 'linear-gradient(90deg, transparent, rgba(138, 109, 8, 0.05))'
        : 'transparent',
      transition: 'background 0.2s',
    }}>
      <Box sx={{
        mt: '3px',
        flexShrink: 0,
        color: isHighPriority ? '#8a6d08' : '#94a3b8',
        display: 'flex',
        alignItems: 'center',
      }}>
        <IconComp size={14} />
      </Box>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.75, flexWrap: 'wrap' }}>
          <Typography sx={{
            fontFamily: 'Frank Ruhl Libre, David, serif',
            fontSize: '0.78rem',
            fontWeight: isHighPriority ? 700 : 600,
            color: '#1e3a5f',
            lineHeight: 1.4,
          }}>
            {item.label}
          </Typography>
          {item.value && (
            <Typography sx={{
              fontFamily: 'Frank Ruhl Libre, David, serif',
              fontSize: '0.73rem',
              color: '#475569',
              lineHeight: 1.4,
            }}>
              {item.value}
            </Typography>
          )}
        </Box>
        {item.detail && (
          <Typography sx={{
            fontFamily: 'Frank Ruhl Libre, David, serif',
            fontSize: '0.63rem',
            color: '#94a3b8',
            lineHeight: 1.5,
            mt: 0.1,
          }}>
            {item.detail}
          </Typography>
        )}
      </Box>
    </Box>
  );
};

// -- קבוצת קטגוריה -------------------------------------------------------------
const CategoryGroup = ({ category, items }) => {
  const label = CATEGORY_LABELS[category] || category;
  const IconComp = CATEGORY_ICONS[category] || Calendar;

  return (
    <Box sx={{ mb: 1.25 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 0.4, px: 0.5 }}>
        <Divider sx={{ flex: 1, borderColor: '#e8edf2' }} />
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <IconComp size={10} style={{ color: '#b0bec5' }} />
          <Typography sx={{
            fontSize: '0.56rem',
            color: '#90a4ae',
            letterSpacing: '0.1em',
            fontFamily: 'Frank Ruhl Libre, David, serif',
            whiteSpace: 'nowrap',
            fontWeight: 600,
          }}>
            {label}
          </Typography>
        </Box>
        <Divider sx={{ flex: 1, borderColor: '#e8edf2' }} />
      </Box>
      {items.map((item, i) => (
        <ItemRow key={item.id || `${category}-${i}`} item={item} />
      ))}
    </Box>
  );
};

// -- יום lookahead בודד --------------------------------------------------------
const LookaheadDay = ({ dayData }) => (
  <Box sx={{
    mt: 1.75,
    pt: 1.25,
    borderTop: '1px dashed #cbd5e1',
  }}>
    <Typography sx={{
      fontFamily: 'Frank Ruhl Libre, David, serif',
      fontSize: '0.72rem',
      fontWeight: 700,
      color: '#1e3a5f',
      textAlign: 'center',
      mb: 0.75,
    }}>
      {dayData.hebrewDate?.hebrew || dayData.date}
    </Typography>
    {dayData.items.length === 0 ? (
      <Typography sx={{
        fontFamily: 'Frank Ruhl Libre, David, serif',
        fontSize: '0.68rem',
        color: '#94a3b8',
        textAlign: 'center',
      }}>
        אין ענייני יום מיוחדים
      </Typography>
    ) : (
      groupByCategory(dayData.items).map(([cat, catItems]) => (
        <CategoryGroup key={cat} category={cat} items={catItems} />
      ))
    )}
  </Box>
);

// -- עזר: קיבוץ לפי קטגוריה ---------------------------------------------------
function groupByCategory(items) {
  const map = {};
  items.forEach((item) => {
    const cat = item.category || 'general';
    if (!map[cat]) map[cat] = [];
    map[cat].push(item);
  });
  return Object.entries(map);
}

// == תבנית מודרנית =============================================================
const Modern = ({
  today,
  items,
  lookaheadItems,
  lookaheadDays,
  loading,
  error,
  title,
  showTitle,
}) => {
  const hebrewDateStr = today?.hebrewDate?.hebrew || '';
  const grouped = groupByCategory(items || []);

  return (
    <Box sx={{
      background: '#ffffff',
      height: '100%',
      display: 'flex',
      flexDirection: 'column',
      direction: 'rtl',
      overflowY: 'auto',
      padding: '14px 12px',
      '&::-webkit-scrollbar': { width: 3 },
      '&::-webkit-scrollbar-thumb': { background: '#e2e8f0', borderRadius: 2 },
    }}>
      {/* כותרת */}
      {showTitle && (
        <Typography sx={{
          fontFamily: 'Frank Ruhl Libre, David, serif',
          fontSize: '0.65rem',
          color: '#90a4ae',
          letterSpacing: '0.15em',
          textAlign: 'center',
          mb: 0.2,
          fontWeight: 500,
        }}>
          {title}
        </Typography>
      )}

      {/* תאריך עברי */}
      {hebrewDateStr && (
        <Box sx={{ textAlign: 'center', mb: 1.25 }}>
          <Typography sx={{
            fontFamily: 'Frank Ruhl Libre, David, serif',
            fontSize: '0.88rem',
            fontWeight: 700,
            color: '#1e3a5f',
            lineHeight: 1.3,
          }}>
            {hebrewDateStr}
          </Typography>
          <Box sx={{
            width: 40,
            height: '1.5px',
            background: 'linear-gradient(90deg, transparent, #8a6d08, transparent)',
            mx: 'auto',
            mt: 0.5,
            borderRadius: 1,
          }} />
        </Box>
      )}

      {/* loading */}
      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 1 }}>
          <CircularProgress size={18} sx={{ color: '#1e3a5f' }} />
        </Box>
      )}

      {/* error */}
      {error && (
        <Typography sx={{
          fontSize: '0.7rem',
          color: '#ef4444',
          textAlign: 'center',
          mb: 1,
        }}>
          שגיאה: {typeof error === 'string' ? error : 'שגיאה בטעינה'}
        </Typography>
      )}

      {/* פריטי היום */}
      {grouped.length === 0 && !loading && (
        <Typography sx={{
          fontFamily: 'Frank Ruhl Libre, David, serif',
          fontSize: '0.75rem',
          color: '#94a3b8',
          textAlign: 'center',
          mt: 2,
        }}>
          אין ענייני יום מיוחדים להיום
        </Typography>
      )}

      {grouped.map(([cat, catItems]) => (
        <CategoryGroup key={cat} category={cat} items={catItems} />
      ))}

      {/* ימי lookahead */}
      {lookaheadDays > 0 && lookaheadItems && lookaheadItems.length > 0 && (
        lookaheadItems.map((dayData, i) => (
          <LookaheadDay key={dayData.date || i} dayData={dayData} />
        ))
      )}
    </Box>
  );
};

export default Modern;
